import React, { useState } from "react";
import Navbar from "../HomePage/Navbar";
import Footer from "../HomePage/Footer";

import {
  MapPin,
  Share2,
  ChevronDown,
  ChevronRight,
  ShieldCheck,
  Zap,
  Globe,
  Sparkles,
  Trees,
  Waves,
  Dumbbell,
  Building2,
  GraduationCap,
  HeartPulse,
  ShoppingBag,
  Plane,
  Briefcase,
  Home,
  Flower2,
} from "lucide-react";

/**
 * FLORIQUE — Project Detail Page
 * -------------------------------------------------------------
 * Drop your photography into /public/images using the filenames
 * referenced in the IMAGES map below. Every image renders through
 * the <Frame> component, so swapping a file is the only step
 * needed — no markup changes required.
 *
 * Install: npm install lucide-react
 * No custom fonts / font-family overrides are used — the page
 * relies on Tailwind's default system font stack.
 * -------------------------------------------------------------
 */
import hero from "./Images/p1.png";
import masterplan from "./Images/p11.png";
import clubhouse from "./Images/p12.png";

const pool = hero;
const sportsZone = masterplan;
const garden = clubhouse;
const towerAlpine = hero;
const towerBlossom = masterplan;

// const IMAGES = {
//   hero: "./Images/p1.png",
//   masterplan: "/images/florique-masterplan.jpg",
//   clubhouse: "/images/florique-clubhouse.jpg",
//   pool: "/images/florique-pool.jpg",
//   sportsZone: "/images/florique-sports.jpg",
//   garden: "/images/florique-garden.jpg",
//   towerAlpine: "/images/florique-tower-alpine.jpg",
//   towerBlossom: "/images/florique-tower-blossom.jpg",
// };

const WHATSAPP_NUMBER = "918088687429";
const MAPS_URL = "https://maps.app.goo.gl/SbmK47wMm21go1dM9?g_st=awb";

const WHATSAPP_MESSAGE = `Hi, I'm interested in *Poulomi Florique*.

📍 Location: ${MAPS_URL}
🌿 8.66 acre IGBC Platinum pre-certified community in Thanisandra, Bengaluru
🏠 3 & 4 BHK residences, 1585 – 2740 sqft (SBUA)
🏊 40+ amenities · 37,800 sqft clubhouse · 27,500 sqft sports zone

Could you share the price list and floor plans?`;

function buildWhatsAppLink() {
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    WHATSAPP_MESSAGE
  )}`;
}

/* ---------- Shared bits ---------- */

function Frame({ src, alt, ratio = "aspect-[4/5]", className = "" }) {
  return (
    <div className={`relative overflow-hidden rounded-3xl bg-gray-100 ${ratio} ${className}`}>
      {/* eslint-disable-next-line jsx-a11y/alt-text */}
      <img
        src={src}
        alt={alt}
        onError={(e) => {
          e.currentTarget.style.display = "none";
          e.currentTarget.nextSibling.style.display = "flex";
        }}
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div
        style={{ display: "none" }}
        className="absolute inset-0 flex items-center justify-center px-4 text-center text-xs font-medium uppercase tracking-wide text-gray-400"
      >
        {alt}
      </div>
    </div>
  );
}

function FloatingBadge({ className = "", children }) {
  return (
    <div
      className={`absolute rounded-2xl bg-white px-5 py-3 shadow-xl shadow-gray-300/40 ${className}`}
    >
      {children}
    </div>
  );
}

function NavPill({ children, active }) {
  return (
    <button
      className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
        active ? "bg-red-50 text-red-600" : "text-gray-700 hover:text-red-600"
      }`}
    >
      {children}
    </button>
  );
}

function ServiceIcon({ icon: Icon, label, isNew }) {
  return (
    <div className="flex flex-shrink-0 flex-col items-center gap-2 text-center">
      <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15">
        {isNew && (
          <span className="absolute -top-2 right-0 rounded-full bg-yellow-400 px-1.5 py-0.5 text-[9px] font-bold uppercase text-red-700">
            New
          </span>
        )}
        <Icon className="h-6 w-6 text-white" strokeWidth={1.8} />
      </div>
      <span className="whitespace-nowrap text-sm font-semibold text-white">{label}</span>
    </div>
  );
}

function ChecklistItem({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-red-50">
        <Icon className="h-5 w-5 text-red-600" strokeWidth={2} />
      </div>
      <span className="text-sm font-semibold text-gray-800 sm:text-base">{children}</span>
    </div>
  );
}

function JumpPill({ icon: Icon, label }) {
  return (
    <button className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-5 py-3 text-sm font-semibold text-gray-800 shadow-sm transition-colors hover:border-red-200 hover:text-red-600">
      <Icon className="h-4 w-4" strokeWidth={2} />
      {label}
      <ChevronRight className="h-3.5 w-3.5" />
    </button>
  );
}

function StatPill({ value, label }) {
  return (
    <div className="rounded-2xl bg-red-50 px-5 py-4 text-center">
      <p className="text-2xl font-extrabold text-red-600">{value}</p>
      <p className="mt-0.5 text-xs font-medium text-gray-500">{label}</p>
    </div>
  );
}

/* ---------- Zone card ---------- */

function ZoneCard({ image, eyebrow, title, items }) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
      <Frame src={image} alt={title} ratio="aspect-[5/4]" />
      <p className="mt-5 text-xs font-bold uppercase tracking-wide text-red-600">{eyebrow}</p>
      <h3 className="mt-1 text-xl font-extrabold text-gray-900">{title}</h3>
      <ul className="mt-4 space-y-2">
        {items.map((it) => (
          <li key={it} className="flex items-center gap-2 text-sm text-gray-600">
            <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-red-400" />
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------- Tower / unit card ---------- */

function TowerCard({ image, name, note, units }) {
  return (
    <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm">
      <Frame src={image} alt={name} ratio="aspect-[16/10]" className="rounded-none" />
      <div className="p-6 sm:p-8">
        <h3 className="text-xl font-extrabold text-gray-900">{name}</h3>
        {note && <p className="mt-1 text-xs italic text-gray-400">{note}</p>}
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
          {units.map((u) => (
            <div key={u.unit} className="rounded-2xl bg-gray-50 p-3">
              <p className="text-[11px] font-bold uppercase tracking-wide text-red-500">
                {u.unit}
              </p>
              <p className="mt-1 text-lg font-extrabold text-gray-900">{u.sbua}</p>
              <p className="text-[10px] font-medium uppercase text-gray-400">sqft sbua</p>
              <p className="mt-1 text-xs font-semibold text-gray-600">{u.config}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- Spec accordion ---------- */

function SpecAccordion({ groups }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="space-y-3">
      {groups.map((g, i) => (
        <div
          key={g.title}
          className={`overflow-hidden rounded-2xl border ${
            open === i ? "border-red-200 bg-red-50/40" : "border-gray-100 bg-white"
          }`}
        >
          <button
            onClick={() => setOpen(open === i ? -1 : i)}
            className="flex w-full items-center justify-between px-6 py-5 text-left"
          >
            <span className="text-base font-bold text-gray-900">{g.title}</span>
            <ChevronDown
              className={`h-4 w-4 text-red-500 transition-transform duration-300 ${
                open === i ? "rotate-180" : ""
              }`}
            />
          </button>
          <div
            className={`grid overflow-hidden transition-all duration-300 ${
              open === i ? "grid-rows-[1fr] pb-6 opacity-100" : "grid-rows-[0fr] opacity-0"
            }`}
            style={{ display: "grid" }}
          >
            <div className="min-h-0 overflow-hidden px-6">
              <ul className="grid gap-2.5 sm:grid-cols-2">
                {g.items.map((it) => (
                  <li key={it} className="flex gap-2 text-sm text-gray-600">
                    <span className="font-bold text-red-500">—</span>
                    {it}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- Connectivity row ---------- */

function ConnectivityGroup({ icon: Icon, title, rows }) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
      <div className="flex items-center gap-2.5">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-red-50">
          <Icon className="h-4.5 w-4.5 text-red-600" strokeWidth={2} />
        </div>
        <h4 className="text-sm font-bold uppercase tracking-wide text-gray-900">{title}</h4>
      </div>
      <ul className="mt-4 space-y-3">
        {rows.map((r) => (
          <li key={r.name} className="flex items-baseline justify-between gap-4 border-b border-gray-100 pb-2.5">
            <span className="text-sm font-medium text-gray-700">{r.name}</span>
            <span className="whitespace-nowrap rounded-full bg-gray-50 px-2.5 py-0.5 text-xs font-bold text-red-500">
              {r.time}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* =====================================================================
   DATA
   ===================================================================== */

const MASTERPLAN_ITEMS = [
  "Clubhouse", "Event Plaza", "Event Lawn", "Amphitheatre Space", "Arrival Garden",
  "Clubhouse Plaza", "Guardhouse", "Palm Island", "Bus Shelter", "Futsal Court",
  "Basketball Court", "Tennis Court", "Cricket Pitch", "Pickleball Court", "Artwork Bench",
  "Fitness Corner", "Lobby Plaza", "Bicycle Parking", "Lobby Garden", "Adventurous Playground",
  "Children's Play Area", "Sit-out Deck", "Changing Room", "Fountain Plaza & Aquatic Pond",
  "Pool Deck", "Pool Pavilion", "Kids' Pool", "Bubbling Pool", "Jacuzzi Pool", "25M Lap Pool",
  "Central Artwork", "Leisure Lawn", "Leisure Deck", "Flower Parterre", "Butterfly Garden",
  "Pergola with Creepers", "Roller Skate Rink", "Gathering Courtyard", "Ginger and Herb Garden",
  "Pet Garden with Walking Track", "Fragrant Garden", "Pebble Garden",
  "Elderly Garden with Reflexology",
];

const CHECKLIST = [
  { icon: ShieldCheck, text: "100% Vastu Compliant" },
  { icon: Home, text: "No Common Walls" },
  { icon: Zap, text: "100% DG Backup" },
  { icon: Globe, text: "IGBC Platinum Certified" },
];

const SERVICES = [
  { icon: Building2, label: "Clubhouse" },
  { icon: Dumbbell, label: "Sports Zone" },
  { icon: Waves, label: "Pools" },
  { icon: Flower2, label: "Gardens", isNew: true },
  { icon: Home, label: "Residences" },
  { icon: ShieldCheck, label: "Specifications" },
  { icon: MapPin, label: "Connectivity" },
  { icon: Share2, label: "Share", isNew: true },
];

const ALPINE_UNITS = [
  { unit: "Unit 01", sbua: "2,535", config: "3BHK + Maid" },
  { unit: "Unit 02", sbua: "2,740", config: "3BHK + Study + Maid" },
  { unit: "Unit 03", sbua: "1,830", config: "3BHK" },
  { unit: "Unit 04", sbua: "1,830", config: "3BHK" },
  { unit: "Unit 05", sbua: "2,210", config: "3BHK + Maid" },
  { unit: "Unit 06", sbua: "2,380", config: "3BHK + Maid" },
];

const BLOSSOM_UNITS = [
  { unit: "Unit 01", sbua: "1,905", config: "3BHK" },
  { unit: "Unit 02", sbua: "2,000", config: "3BHK" },
  { unit: "Unit 03", sbua: "1,585", config: "3BHK · 2 toilets" },
  { unit: "Unit 04", sbua: "1,780", config: "3BHK" },
  { unit: "Unit 05", sbua: "1,585", config: "3BHK · 2 toilets" },
  { unit: "Unit 06", sbua: "1,780", config: "3BHK" },
  { unit: "Unit 07", sbua: "1,890", config: "3BHK" },
  { unit: "Unit 08", sbua: "2,030", config: "3BHK" },
];

const SPEC_GROUPS = [
  {
    title: "Structure & Walls",
    items: [
      "RCC structural shear wall system, engineered for wind & seismic loads per IS codes",
      "Living, dining & bedroom walls in gypsum punning, putty & low-VOC emulsion paint",
      "Bathroom walls in vitrified tile cladding, 600×1200mm of reputed make",
      "Balcony walls tiled to 4ft height, painted above the dado",
    ],
  },
  {
    title: "Flooring",
    items: [
      "Living, dining & kitchen: vitrified tiles, 800×1600mm, reputed make",
      "Master bedroom & closet: vitrified tiles, 600×1200mm",
      "Other bedrooms & bathrooms: vitrified tiles, 600×1200mm / 600×600mm",
      "Utility & balconies: matte-finish vitrified tiles, 600×600mm",
    ],
  },
  {
    title: "Doors, Windows & Access",
    items: [
      "8ft height engineered wood main entrance door, melamine-polished both sides",
      "UPVC window system with float glass & mosquito mesh provision",
      "8ft UPVC sliding doors at balconies & sit-outs",
      "Main door integrated with a digital smart lock of reputed make",
    ],
  },
  {
    title: "Smart Systems & MEP",
    items: [
      "3-phase electrical supply with MCB distribution boards",
      "Reticulated piped gas supply with auto shut-off & leak detection",
      "Heat & smoke sensors in kitchen, fire sprinklers per regulation",
      "App-based central communication system; FTTH points in living & bedrooms",
    ],
  },
  {
    title: "Block & Common Areas",
    items: [
      "Double-height entrance lounge with basement & podium-level access",
      "4 high-speed passenger elevators + 1 service elevator per tower",
      "Sewage treatment plant; treated water reused for landscaping & flushing",
      "Centralised BMS, sensor/timer-based lighting & 100% DG power backup",
    ],
  },
];

const CONNECTIVITY = [
  {
    icon: GraduationCap,
    title: "Education",
    rows: [
      { name: "Chaman Bhartiya School", time: "5 min" },
      { name: "Delhi Public School, Bangalore North", time: "15 min" },
      { name: "REVA University", time: "15 min" },
      { name: "Orchids The International School", time: "15 min" },
    ],
  },
  {
    icon: HeartPulse,
    title: "Healthcare",
    rows: [
      { name: "Kriyashila Hospitals, Kothanur", time: "10 min" },
      { name: "North Bangalore Hospital", time: "10 min" },
      { name: "Bangalore Baptist Hospital", time: "10 min" },
      { name: "Aster CMI Hospital", time: "15 min" },
    ],
  },
  {
    icon: Briefcase,
    title: "Tech Parks",
    rows: [
      { name: "Bharatiya Centre of Information Technology", time: "10 min" },
      { name: "Manyata Tech Park", time: "15 min" },
    ],
  },
  {
    icon: ShoppingBag,
    title: "Lifestyle",
    rows: [
      { name: "Bhartiya Mall of Bengaluru", time: "15 min" },
      { name: "Elements Mall", time: "15 min" },
      { name: "Leela Hotel & Residences", time: "30 min" },
    ],
  },
  {
    icon: Plane,
    title: "Transit",
    rows: [{ name: "Kempegowda International Airport", time: "40 min" }],
  },
];

/* =====================================================================
   PAGE
   ===================================================================== */

export default function FloriqueProjectPage() {
  return (
    <div className="bg-white text-gray-900">
        <Navbar/>
      {/* Header */}
      {/* <header className="flex items-center justify-between gap-4 px-5 py-4 sm:px-10">
        <div className="flex items-center gap-1.5 text-xl font-extrabold">
          <Flower2 className="h-6 w-6 text-red-600" />
          <span>
            Poulomi <span className="text-red-600">Florique</span>
          </span>
        </div>

        <nav className="hidden items-center gap-1 rounded-full border border-gray-100 bg-white px-2 py-1.5 shadow-sm lg:flex">
          <NavPill active>Home</NavPill>
          <NavPill>Amenities</NavPill>
          <NavPill>Residences</NavPill>
          <NavPill>Specifications</NavPill>
          <NavPill>Connectivity</NavPill>
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={MAPS_URL}
            target="_blank"
            rel="noreferrer"
            className="hidden items-center gap-2 rounded-full border border-red-200 px-5 py-2.5 text-sm font-bold text-red-600 transition-colors hover:bg-red-50 sm:flex"
          >
            <MapPin className="h-4 w-4" />
            Location
          </a>
          <a
            href={buildWhatsAppLink()}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 rounded-full bg-green-500 px-5 py-2.5 text-sm font-bold text-white shadow-md shadow-green-200 transition-colors hover:bg-green-600"
          >
            <Share2 className="h-4 w-4" />
            WhatsApp
            <span className="h-2 w-2 rounded-full bg-white" />
          </a>
        </div>
      </header> */}

      {/* Services strip */}
      {/* <div className="overflow-x-auto bg-gradient-to-r from-red-600 to-red-500 px-5 py-6 sm:px-10">
        <div className="flex min-w-max items-start justify-center gap-10 sm:gap-14">
          {SERVICES.map((s) => (
            <ServiceIcon key={s.label} {...s} />
          ))}
        </div>
      </div> */}

      {/* Hero */}
      <section className="bg-gradient-to-b from-red-50/70 via-red-50/20 to-white px-5 py-14 sm:px-10 sm:py-20 -mt-24">
        <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-2 lg:items-center">
          {/* Left */}
          <div>
            <p className="text-sm font-bold uppercase tracking-wide text-red-600">Feature Project</p>
            <h1 className="mt-3 text-5xl font-extrabold leading-[1.05] text-gray-900 sm:text-6xl">
              Poulomi 
              <br />
              <span className="text-red-600">Florique</span>
            </h1>
            <p className="mt-6 max-w-md text-base leading-relaxed text-gray-600">
              At Florique, we design 8.66 acres of IGBC Platinum living that
              connects you to verified residences, prime amenities, and
              seamless everyday comfort — every detail built to help you flourish.
            </p>

            <div className="mt-8 grid grid-cols-2 gap-5">
              {CHECKLIST.map((c) => (
                <ChecklistItem key={c.text} icon={c.icon}>
                  {c.text}
                </ChecklistItem>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <JumpPill icon={Building2} label="Amenities" />
              <JumpPill icon={Home} label="Residences" />
              <JumpPill icon={ShieldCheck} label="Specifications" />
              <JumpPill icon={MapPin} label="Connectivity" />
            </div>
          </div>

          {/* Right */}
          <div className="relative">
            <Frame src={hero} alt="Florique — facade & living" ratio="aspect-[4/3]" />
            <FloatingBadge className="-left-3 top-6 sm:-left-6">
              <p className="text-sm font-extrabold text-gray-900">8.66 Acres</p>
              <p className="text-xs text-gray-500">IGBC Platinum</p>
            </FloatingBadge>
            <FloatingBadge className="-bottom-5 -right-3 sm:-right-6">
              <p className="text-xl font-extrabold text-red-600">40+</p>
              <p className="text-xs text-gray-500">Amenities Onsite</p>
            </FloatingBadge>
          </div>
        </div>
      </section>

      {/* Zones */}
      <section className="px-5 py-16 sm:px-10 sm:py-20">
        <div className="mx-auto max-w-6xl">
          <p className="text-sm font-bold uppercase tracking-wide text-red-600">
            Where Energy Finds Its Space
          </p>
          <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Three worlds, one address.
          </h2>
          <div className="mt-10 grid gap-8 sm:grid-cols-3">
            <ZoneCard
              image={sportsZone}
              eyebrow="27,500 sqft"
              title="Sport & Wellness Zone"
              items={["Futsal court", "Pickleball court", "Basketball court", "Tennis court", "Cricket pitch"]}
            />
            <ZoneCard
              image={clubhouse}
              eyebrow="37,800 sqft"
              title="The Clubhouse"
              items={["Multipurpose hall", "Party lounge", "Co-working spaces", "Badminton & squash", "40+ amenities"]}
            />
            <ZoneCard
              image={garden}
              eyebrow="Outdoor"
              title="Gardens For Stillness"
              items={["Reflexology garden", "Butterfly garden", "Outdoor yoga deck", "Leisure lawn", "Gathering courtyard"]}
            />
          </div>
        </div>
      </section>

      {/* Stat strip */}
      <section className="px-5 sm:px-10">
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-4 sm:grid-cols-5">
          <StatPill value="8.66" label="Acres" />
          <StatPill value="43" label="Masterplan Elements" />
          <StatPill value="37.8K" label="Sqft Clubhouse" />
          <StatPill value="27.5K" label="Sqft Sports Zone" />
          <StatPill value="14" label="Unit Layouts" />
        </div>
      </section>

      {/* Masterplan legend */}
      <section className="px-5 py-16 sm:px-10 sm:py-20">
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:items-start">
            <div>
              <p className="text-sm font-bold uppercase tracking-wide text-red-600">The Masterplan</p>
              <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
                43 elements, one continuous landscape.
              </h2>
              <p className="mt-4 max-w-sm text-sm leading-relaxed text-gray-600">
                Every numbered marker on the site plan corresponds to a planned
                space below — from the arrival garden to the elderly
                reflexology garden at the far end of the grounds.
              </p>
              <Frame src={masterplan} alt="Florique site masterplan" ratio="aspect-[4/3]" className="mt-8" />
            </div>
            <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm sm:p-8">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
                {MASTERPLAN_ITEMS.map((item, i) => (
                  <div key={item} className="flex gap-2 border-b border-gray-100 pb-2.5">
                    <span className="text-xs font-extrabold text-red-500">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-xs leading-tight text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pool strip */}
      <section className="px-5 sm:px-10">
        <div className="mx-auto max-w-6xl">
          <Frame src={pool} alt="25M lap pool & pool deck" ratio="aspect-[16/7]" />
        </div>
      </section>

      {/* Residences */}
      <section className="px-5 py-16 sm:px-10 sm:py-20">
        <div className="mx-auto max-w-6xl">
          <p className="text-sm font-bold uppercase tracking-wide text-red-600">Residences</p>
          <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Homes tailored for life's finest moments.
          </h2>
          <div className="mt-10 grid gap-8 lg:grid-cols-2">
            <TowerCard
              image={towerAlpine}
              name="Tower Alpine & Tower Dewdrop"
              note="Identical layouts — the only variation is in the core plan."
              units={ALPINE_UNITS}
            />
            <TowerCard image={towerBlossom} name="Tower Blossom & Tower Cascade" units={BLOSSOM_UNITS} />
          </div>
        </div>
      </section>

      {/* Specifications */}
      <section className="bg-gray-50 px-5 py-16 sm:px-10 sm:py-20">
        <div className="mx-auto max-w-4xl">
          <p className="text-sm font-bold uppercase tracking-wide text-red-600">Specifications</p>
          <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Quality that runs deeper than the surface.
          </h2>
          <div className="mt-10">
            <SpecAccordion groups={SPEC_GROUPS} />
          </div>
        </div>
      </section>

      {/* Connectivity */}
      <section className="px-5 py-16 sm:px-10 sm:py-20">
        <div className="mx-auto max-w-6xl">
          <p className="text-sm font-bold uppercase tracking-wide text-red-600">
            Everyday Calm In The Heart Of The City
          </p>
          <h2 className="mt-2 max-w-lg text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Thanisandra, with Bengaluru close at hand.
          </h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {CONNECTIVITY.map((c) => (
              <ConnectivityGroup key={c.title} {...c} />
            ))}
          </div>
          <a
            href={MAPS_URL}
            target="_blank"
            rel="noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-red-600 px-6 py-3.5 text-sm font-bold text-white shadow-md shadow-red-200 transition-colors hover:bg-red-700"
          >
            <MapPin className="h-4 w-4" />
            Open in Google Maps
          </a>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="bg-gradient-to-r from-red-600 to-red-500 px-5 py-16 text-center sm:px-10 sm:py-20">
        <Sparkles className="mx-auto h-7 w-7 text-white" strokeWidth={1.6} />
        <h2 className="mx-auto mt-4 max-w-xl text-3xl font-extrabold text-white sm:text-4xl">
          Live Vivid. Live Vibrant.
        </h2>
        <p className="mx-auto mt-3 max-w-md text-sm text-red-50">
          Get the price list, floor plans & a site visit slot on WhatsApp.
        </p>
        <div className="mt-7 flex flex-wrap justify-center gap-3">
          <a
            href="https://wa.me/message/MDG2ZSZNG6P6G1"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-bold text-red-600 shadow-lg transition-colors hover:bg-red-50"
          >
            Contact & Enquire
          </a>
          <a
            href={buildWhatsAppLink()}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-red-700 px-7 py-3.5 text-sm font-bold text-white shadow-lg transition-colors hover:bg-red-800"
          >
            <Share2 className="h-4 w-4" />
            Share on WhatsApp
          </a>
        </div>
        <p className="mt-4 text-xs font-semibold text-red-100">+91 80886 87429</p>
      </section>

      <footer className="px-5 py-8 text-center sm:px-10">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
          Poulomi Florique · Thanisandra, Bengaluru
        </p>
      </footer>

      {/* Floating WhatsApp button */}
      <a
        href={buildWhatsAppLink()}
        target="_blank"
        rel="noreferrer"
        aria-label="Share project details on WhatsApp"
        className="fixed bottom-6 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-green-500 text-white shadow-xl shadow-green-300/50 transition-transform hover:scale-105 sm:bottom-8 sm:right-8"
      >
        <Share2 className="h-5 w-5" />
      </a>
      <Footer/>
    </div>
  );
}